import React from 'react';
import { Pagination, PaginationProps, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { Flex } from '../../../common/layout/helpers';

// Due to an Elasticsearch config limitation, we can't paginate past 10000 documents
// See max_result_window in https://www.elastic.co/guide/en/elasticsearch/reference/current/index-modules.html
const MAX_RESULTS_WINDOW = 10000;

type TablePaginationProps = {
  page: number;
  limit: number;
  total: number;
  boundaryRange?: number;
  onPageChange: (
    e: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => void;
  className?: string;
  estimated?: boolean;
};

export function TablePagination({
  page,
  limit,
  total,
  boundaryRange = 0,
  onPageChange,
  className,
  estimated,
}: TablePaginationProps) {
  const { t } = useTranslation();

  const totalPages = Math.ceil(total / limit);
  const maxPage = Math.ceil(MAX_RESULTS_WINDOW / limit);
  const displayedTotalPages = Math.min(totalPages, maxPage);

  return (
    <Flex justify="center">
      {totalPages > 1 && (
        <Pagination
          title={
            estimated
              ? t(
                  'pagination.estimated',
                  'Due to performance reasons these pages are an (over)estimate'
                )
              : undefined
          }
          className={className}
          nextItem={
            page * limit - total < 0
              ? {
                  'aria-label': 'Next Page',
                  content: t('Pagination.next', 'Next'),
                }
              : null
          }
          prevItem={
            page !== 1
              ? {
                  'aria-label': 'Previous item',
                  content: t('Pagination.previous', 'Previous'),
                }
              : null
          }
          onPageChange={onPageChange}
          activePage={page}
          firstItem={null}
          siblingRange={3}
          boundaryRange={boundaryRange}
          lastItem={null}
          totalPages={displayedTotalPages}
        />
      )}
      {page === displayedTotalPages && totalPages > displayedTotalPages && (
        <Message
          content={t(
            'pagination.maxPaginationMessage',
            `Can't find what you're looking for? Try refining your search.`
          )}
        />
      )}
    </Flex>
  );
}
